/** Batch-local ordered-array access. PHP casts and copies remain canonical. */
import { PhpArray, PhpFloat, floatval, fromJson, identical, type PhpKeyInput } from '../../php-compat';
import { portWithWrites, portWritesBrand, type PortEnv, type PortWithWrites, type PortWriteIntent, type PhpRef } from '../../port-contract';
/** Bubble persistence intents from supplied callees without executing writes.
 * Each callback still returns its PHP value to the caller's decision code. */
export function collectWrites<T, Deps extends object>(env: PortEnv<Deps>, run: (env: PortEnv<Deps>, collect: (result: unknown) => unknown) => T): T | PortWithWrites<T> {
    const writes: PortWriteIntent[] = [];
    const unwrap = (result: unknown): unknown => {
        if (result !== null && typeof result === 'object' && portWritesBrand in result && result[portWritesBrand] === true) {
            const wrapped = result as PortWithWrites<unknown>;
            writes.push(...wrapped.writes);
            return wrapped.value;
        }
        return result;
    };
    const deps = new Proxy(env.deps, {
        get(target, key) {
            const callback = Reflect.get(target, key);
            return typeof callback === 'function' ? (...args: unknown[]) => unwrap(Reflect.apply(callback, target, args)) : callback;
        },
    });
    const value = unwrap(run({ ...env, deps }, unwrap)) as T;
    return writes.length ? portWithWrites(value, writes) : value;
}
export const isArray = (v: unknown): v is object => v !== null && typeof v === 'object' && !(v instanceof PhpFloat);
export function array(v: unknown): PhpArray { return v instanceof PhpArray ? v : fromJson(v) as PhpArray; }
export function at(v: unknown, ...keys: PhpKeyInput[]): unknown {
    for (const k of keys) {
        if (!isArray(v))
            return null;
        v = array(v).get(k);
    }
    return v ?? null;
}
export function put(v: unknown, keys: PhpKeyInput[], value: unknown): void {
    const a = array(v), k = keys[0]!;
    if (keys.length === 1) {
        a.set(k, value);
        return;
    }
    if (!isArray(a.get(k)))
        a.set(k, new PhpArray());
    const child = array(a.get(k));
    a.set(k, child);
    put(child, keys.slice(1), value);
}
export const num = (v: unknown): number => Number(floatval(v));
export const f = (v: number): PhpFloat => new PhpFloat(v);
export const list = (v: unknown[]): PhpArray => new PhpArray(v.map((x, i) => [i, x]));
export const record = (v: Record<string, unknown>): PhpArray => new PhpArray(Object.entries(v));
export const values = (v: unknown): unknown[] => isArray(v) ? [...array(v).values()] : [];
export function fail(name: string, message: string): never { const e = new Error(message); e.name = name; throw e; }
/** Rebind a PHP reference-return dependency to its row in the supplied array. */
export function returnedRef(parent: PhpRef<unknown>, value: unknown): PhpRef<unknown> {
    if (value === null)
        return { value: null };
    parent.value = array(parent.value);
    const find = (container: PhpArray): PhpRef<unknown> | null => {
        for (const [k, child] of container) {
            if (!isArray(child))
                continue;
            if (identical(child, value))
                return { get value() { return container.get(k); }, set value(v) { container.set(k, v); } };
            const nested = array(child);
            container.set(k, nested);
            const ref = find(nested);
            if (ref)
                return ref;
        }
        return null;
    };
    return find(array(parent.value)) ?? { value };
}
/** Adapter for stateless geometry twins; page readers never pass through it. */
export function native(v: unknown): unknown {
    if (v instanceof PhpFloat)
        return v.value;
    if (v instanceof PhpArray) {
        const entries = [...v];
        return entries.every(([k], i) => k === i) ? entries.map(([, x]) => native(x)) : Object.fromEntries(entries.map(([k, x]) => [k, native(x)]));
    }
    if (Array.isArray(v))
        return v.map(native);
    return isArray(v) ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, native(x)])) : v;
}
export function floatTree(v: unknown, integerKeys: readonly string[] = []): unknown {
    if (typeof v === 'number')
        return f(v);
    if (Array.isArray(v))
        return list(v.map(x => floatTree(x, integerKeys)));
    return isArray(v) ? record(Object.fromEntries(Object.entries(v).map(([k, x]) => [k, integerKeys.includes(k) ? x : floatTree(x, integerKeys)]))) : v;
}
/** Synchronous SHA-256 for PHP hash-based receipt ids (no clock or platform state). */
export function sha256(text: string): string {
    const bytes: number[] = [];
    for (const char of text) {
        const n = char.codePointAt(0)!;
        if (n < 128)
            bytes.push(n);
        else if (n < 2048)
            bytes.push(192 | n >> 6, 128 | n & 63);
        else if (n < 65536)
            bytes.push(224 | n >> 12, 128 | n >> 6 & 63, 128 | n & 63);
        else
            bytes.push(240 | n >> 18, 128 | n >> 12 & 63, 128 | n >> 6 & 63, 128 | n & 63);
    }
    const length = bytes.length * 8;
    bytes.push(128);
    while (bytes.length % 64 !== 56)
        bytes.push(0);
    for (let i = 7; i >= 0; i--)
        bytes.push(Math.floor(length / 2 ** (i * 8)) & 255);
    const h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
    const k = [0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2];
    const r = (v: number, n: number) => v >>> n | v << (32 - n);
    for (let offset = 0; offset < bytes.length; offset += 64) {
        const w: number[] = [];
        for (let i = 0; i < 16; i++)
            w.push(bytes[offset + i * 4]! << 24 | bytes[offset + i * 4 + 1]! << 16 | bytes[offset + i * 4 + 2]! << 8 | bytes[offset + i * 4 + 3]!);
        for (let i = 16; i < 64; i++) {
            const a = w[i - 15]!, b = w[i - 2]!;
            w.push((w[i - 16]! + (r(a, 7) ^ r(a, 18) ^ a >>> 3) + w[i - 7]! + (r(b, 17) ^ r(b, 19) ^ b >>> 10)) | 0);
        }
        let [a, b, c, d, e, g, j, l] = h as [
            number,
            number,
            number,
            number,
            number,
            number,
            number,
            number
        ];
        for (let i = 0; i < 64; i++) {
            const t = (l + (r(e, 6) ^ r(e, 11) ^ r(e, 25)) + ((e & g) ^ (~e & j)) + k[i]! + w[i]!) | 0;
            const u = ((r(a, 2) ^ r(a, 13) ^ r(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
            l = j;
            j = g;
            g = e;
            e = (d + t) | 0;
            d = c;
            c = b;
            b = a;
            a = (t + u) | 0;
        }
        [a, b, c, d, e, g, j, l].forEach((v, i) => { h[i] = (h[i]! + v) | 0; });
    }
    return h.map(v => (v >>> 0).toString(16).padStart(8, '0')).join('');
}
export interface TransferDeps {
    'TeamType::getId'(name: string): unknown;
    'StatusEffect::getId'(name: string): unknown;
    encounter_creature_field(row: unknown, key: string): unknown;
    encounter_field(row: unknown, key: string): unknown;
    inventory_field(row: unknown, key: string): unknown;
    layer_value(row: unknown, key: string): unknown;
    encounter_row_team_uuid(row: unknown): unknown;
    get_ground_z_for_object(row: unknown): unknown;
    clamp_object_center_to_encounter_visible_bounds(encounter: unknown, row: unknown, x: unknown, z: unknown): unknown;
    find_free_position(encounter: unknown, row: unknown, x: unknown, z: unknown): unknown;
    check_uuid(id: unknown): boolean;
    advance_turn_if_dying_creatures_turn(row: PhpRef<unknown>, affected: PhpRef<unknown>): unknown;
    ft_encounter_id_is_depth_authored(id: string): boolean;
    ft_encounter_shape(row: unknown): unknown;
    ft_placement_ground_z(id: string, row: unknown, shape: unknown, x: unknown, y: unknown): unknown;
    set_pos_coord(row: PhpRef<unknown>, key: string, value: unknown): unknown;
    pos_coord(row: unknown, key: string): unknown;
    ft_resolve_move_destination(encounter: unknown, row: unknown, x: unknown, z: unknown, y: unknown, free: boolean): unknown;
    update_encounter_creature_stats(row: PhpRef<unknown>, updates: unknown, affected: PhpRef<unknown>): unknown;
    ft_gm_relocation_update_creature(row: PhpRef<unknown>, updates: unknown, affected: PhpRef<unknown>): unknown;
    encounter_member_transfer(source: PhpRef<unknown>, destination: PhpRef<unknown>, id: string, row: unknown, hint: string | null): unknown;
    has_status_effect(row: unknown, id: unknown): boolean;
    query_encounter_arr(id: unknown): unknown;
    check_caster_piloting_sync(caster: PhpRef<unknown>, encounter: PhpRef<unknown>, sx: unknown, sz: unknown, x: unknown, z: unknown, force: boolean): unknown;
    ft_encounter_roster_row_ref(encounter: PhpRef<unknown>, id: string): unknown;
    inventory_set(row: PhpRef<unknown>, key: string, value: unknown): unknown;
    update_inventory_stats(row: unknown, updates: unknown, affected: PhpRef<unknown>): unknown;
    encounter_inventory_remove(source: PhpRef<unknown>, id: string, hint: string | null): unknown;
    encounter_inventory_place(encounter: PhpRef<unknown>, row: unknown): unknown;
    handle_riding_mounting_positioning(caster: PhpRef<unknown>, target: PhpRef<unknown>, encounter: PhpRef<unknown>, affected: PhpRef<unknown>, roll: string): unknown;
    ft_encounter_transfer_strip_queue(id: string, creatures: unknown, inventories: unknown): unknown;
    inventory_is_passage_prop(row: unknown): boolean;
    item_field(row: unknown, key: string): unknown;
    get_base_size(row: unknown): unknown;
    ft_swarm_occupied_width_ft(row: unknown, base: unknown): unknown;
    get_object_height_feet(row: unknown): unknown;
}
